import { useState } from 'react'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { Switch } from '@/components/shadcn/switch'
import { useRestClient } from '@/contexts/QueryProvider'
import { useSessionStorage } from '@/hooks'
import { isPlatformAdmin, isProjectAdmin, isProjectLeader, isSuperUser } from '@/util'
import { toast } from 'sonner'

interface ApiAccessToggleProps {
  userId: number
  isEnabled: boolean
}

export function ApiAccessToggle({ userId, isEnabled }: ApiAccessToggleProps) {
  const [roles] = useSessionStorage('roles', 0)
  const [authCode, setAuthCode] = useState<string | null>(null)
  const [isAuthCodeDialogOpen, setIsAuthCodeDialogOpen] = useState(false)
  const { postExternalUserAdd, deleteExternalUserDelete, getSupportApiGetPlatformUsers } = useRestClient()

  const onMutationSuccess = () => getSupportApiGetPlatformUsers.invalidateQueries()
  const getAuthCodeFromResponse = (response: unknown): string | undefined => {
    if (!response || typeof response !== 'object') {
      return undefined
    }

    const authCodeValue = (response as { authCode?: unknown }).authCode
    return typeof authCodeValue === 'string' ? authCodeValue : undefined
  }

  const allAccess = isSuperUser(roles) || isPlatformAdmin(roles)
  const canToggleApiAccess = isProjectLeader(roles) || isProjectAdmin(roles) || allAccess

  const { mutate: enableApiAccess, isPending: isEnabling } = postExternalUserAdd.useMutation(undefined, {
    onSuccess: (response) => {
      const returnedAuthCode = getAuthCodeFromResponse(response)
      if (returnedAuthCode) {
        setAuthCode(returnedAuthCode)
        setIsAuthCodeDialogOpen(true)
      }
      toast.success('API access enabled successfully.')
      onMutationSuccess()
    },
    onError: () => {
      toast.error('Failed to enable API access.')
    },
  })

  const { mutate: disableApiAccess, isPending: isDisabling } = deleteExternalUserDelete.useMutation(undefined, {
    onSuccess: () => {
      toast.success('API access disabled successfully.')
      onMutationSuccess()
    },
    onError: () => {
      toast.error('Failed to disable API access.')
    },
  })

  const isPending = isEnabling || isDisabling
  const disabled = !canToggleApiAccess || isPending

  const handleToggle = (checked: boolean) => {
    if (checked) {
      enableApiAccess({ body: { userId } })
    } else {
      disableApiAccess({ path: { userId } })
    }
  }

  return (
    <>
      <Switch checked={isEnabled} onCheckedChange={handleToggle} disabled={disabled} aria-label="Toggle API access" />
      <ConfirmDialog
        isOpen={isAuthCodeDialogOpen}
        onOpenChange={setIsAuthCodeDialogOpen}
        title="API access enabled"
        description={authCode ? `Auth code: ${authCode}` : undefined}
        confirmLabel="Close"
        cancelLabel="Close"
        onConfirm={() => setIsAuthCodeDialogOpen(false)}
        onCancel={() => setIsAuthCodeDialogOpen(false)}
      />
    </>
  )
}
